/**
 * QA tracker — bugs and enhancements found while testing, assigned to a
 * teammate and worked through Backlog -> In Progress -> Testable -> Archived
 * (Done | Deferred | QA Found Fixed), with a TBD side-list for anything a
 * developer genuinely could not reproduce.
 *
 * A developer finishing their own work moves an item to TESTABLE, never
 * straight to Done — "I believe this is fixed" and "QA has verified this is
 * fixed" are different claims, and only the second one should close a
 * ticket. QA works from the Testable list and is the one who marks it Done.
 *
 * TBD is not a resolution and never archives anything — nothing has been
 * resolved, the item just needs more information (a clearer repro, an
 * environment difference sorted out) before real work can start on it again.
 *
 * Deliberately one flat list per stage rather than a drag-and-drop board: the
 * status changes ARE the workflow (task said "when someone marks it..."), so a
 * button that names the next step reads clearer than a card someone drags, and
 * it's the same list/detail shape as Reports and Tests — one less pattern to learn.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type QaActivityEntry, type QaItem, type QaItemDraft, type QaOptions, type QaStatus } from '../api';

const TYPE_PILL: Record<string, string> = { bug: 'pill-fail', enhancement: 'pill-accent' };
const PRIORITY_PILL: Record<string, string> = { critical: 'pill-fail', high: 'pill-warn', medium: 'pill-info', low: 'pill-info' };
/** Done, Deferred and QA Found Fixed all land in Archived, so the list view is
 *  the one place they'd otherwise look identical — this is the whole reason a
 *  distinct pill matters here, not just in the detail panel. */
const RESOLUTION_PILL: Record<string, string> = { done: 'pill-pass', deferred: 'pill-warn', qa_found_fixed: 'pill-info' };
const RESOLUTION_LABEL: Record<string, string> = { done: 'Done', deferred: 'Deferred', qa_found_fixed: 'QA found fixed' };

const STATUS_TABS: Array<{ id: QaStatus; label: string }> = [
  { id: 'backlog', label: 'Backlog' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'testable', label: 'Testable' },
  { id: 'tbd', label: 'TBD' },
  { id: 'archived', label: 'Archived' },
];

/** Mirrors the server's transition table (qaTrackerRoutes.ts) so the right
 *  button(s) show up — the server still refuses anything not actually allowed,
 *  exactly as `start()` in api.ts never duplicates the production rule, only
 *  the common-case shape of it. */
const NEXT_ACTIONS: Record<QaStatus, Array<{ target: 'in_progress' | 'testable' | 'tbd' | 'done' | 'deferred' | 'qa_found_fixed' | 'backlog'; label: string; tone: 'primary' | 'secondary' }>> = {
  backlog: [
    { target: 'in_progress', label: 'Start — In Progress', tone: 'primary' },
    // Reachable straight from Backlog too: QA can find an item no longer
    // reproduces before anyone has even picked it up.
    { target: 'qa_found_fixed', label: 'QA Found Fixed', tone: 'secondary' },
    { target: 'tbd', label: 'Could Not Reproduce — TBD', tone: 'secondary' },
  ],
  in_progress: [
    // The developer's own "done" — moves to Testable, never straight to
    // Done. QA is who marks it Done, from the Testable list below.
    { target: 'testable', label: 'Mark Testable', tone: 'primary' },
    { target: 'deferred', label: 'Mark Deferred', tone: 'secondary' },
    { target: 'qa_found_fixed', label: 'QA Found Fixed', tone: 'secondary' },
    { target: 'tbd', label: 'Could Not Reproduce — TBD', tone: 'secondary' },
    { target: 'backlog', label: 'Back to Backlog', tone: 'secondary' },
  ],
  testable: [
    { target: 'done', label: 'Mark Done', tone: 'primary' },
    // QA verified it and it's NOT actually fixed — back to active work,
    // rather than sitting in Testable where nobody is looking at it again.
    { target: 'in_progress', label: 'Not Fixed — Back to In Progress', tone: 'secondary' },
    { target: 'backlog', label: 'Back to Backlog', tone: 'secondary' },
  ],
  tbd: [
    { target: 'backlog', label: 'Back to Backlog', tone: 'primary' },
    { target: 'in_progress', label: 'Retry — In Progress', tone: 'secondary' },
  ],
  archived: [{ target: 'backlog', label: 'Reopen to Backlog', tone: 'secondary' }],
};

function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A tiny spinner + optional label — swapped in for a button's normal
 *  contents while its own action is in flight, so a slow request reads as
 *  "working", never "did my click even land?". */
function Spinner({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="spinner" aria-hidden="true" />
      {label}
    </span>
  );
}

/** The first image attachment, if any — a small thumbnail so a bug with a
 *  screenshot is recognizable at a glance in the list, without opening it.
 *  Only the first: a row is a single line of context, not a gallery. */
function firstImageAttachment(item: QaItem): { url: string } | null {
  const image = item.attachments.find((a) => a.contentType?.startsWith('image/'));
  return image ? { url: api.qaAttachmentUrl(item.id, image.key) } : null;
}

export function TrackerScreen({ reporterEmail }: { reporterEmail: string }) {
  const [options, setOptions] = useState<QaOptions | null>(null);
  const [statusTab, setStatusTab] = useState<QaStatus>('backlog');

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('');
  const [itemType, setItemType] = useState('');
  const [module, setModule] = useState('');
  const [reporter, setReporter] = useState('');

  const [items, setItems] = useState<QaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<QaItem | null>(null);
  const [activity, setActivity] = useState<QaActivityEntry[]>([]);

  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; problems: Array<{ row: number; message: string }> } | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const bump = () => setRefreshToken((n) => n + 1);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  /** Which single action is currently in flight, e.g. "assign", "save",
   *  "transition:done", "delete-attachment:<key>" — disables and spinners
   *  only the ONE button that was actually clicked, not the whole panel. */
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** How many items match the CURRENT filters in each status — fetched
   *  alongside the active tab's own list, so a filter that looks like it
   *  "misses" items shows exactly where the rest actually are instead of
   *  silently only ever searching the one tab currently open. */
  const [tabCounts, setTabCounts] = useState<Record<QaStatus, number> | null>(null);

  useEffect(() => {
    api.qaOptions().then(setOptions).catch(() => {});
  }, [refreshToken]);

  // Debounced so every keystroke in the title search isn't its own round trip —
  // same reasoning TestsScreen gives for debouncing validation as you type.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const activeFilters = {
    assignee: assignee || undefined,
    priority: priority || undefined,
    itemType: itemType || undefined,
    module: module || undefined,
    reporter: reporter || undefined,
    search: search || undefined,
  };
  const filtersKey = JSON.stringify(activeFilters);

  useEffect(() => {
    setLoading(true);
    api.qaItems(statusTab, activeFilters)
      .then((r) => setItems(r.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, filtersKey, refreshToken]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(STATUS_TABS.map((t) => api.qaItems(t.id, activeFilters).then((r) => [t.id, r.items.length] as const)))
      .then((pairs) => { if (!cancelled) setTabCounts(Object.fromEntries(pairs) as Record<QaStatus, number>); })
      .catch(() => { if (!cancelled) setTabCounts(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, refreshToken]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); setActivity([]); return; }
    api.qaItem(selectedId)
      .then((r) => { setDetail(r.item); setActivity(r.activity); })
      .catch(() => { setDetail(null); setActivity([]); });
  }, [selectedId, refreshToken]);

  const modules = useMemo(() => options?.modules ?? [], [options]);

  /** Returns whether it actually succeeded — a caller that needs to know
   *  (the Edit/Save toggle only leaves edit mode on success, so a failed
   *  save never silently discards the draft the client was still looking
   *  at) can await this; one that doesn't just fires it and moves on. */
  async function runAction(key: string, fn: () => Promise<unknown>): Promise<boolean> {
    setNotice(null);
    setBusyKey(key);
    try {
      await fn();
      bump();
      return true;
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : String(e));
      return false;
    } finally {
      setBusyKey(null);
    }
  }

  async function remove(id: number, displayId: string, title: string) {
    if (!window.confirm(`Delete ${displayId} — "${title}"? This cannot be undone.`)) return;
    setDeletingId(id);
    setNotice(null);
    try {
      await api.deleteQaItem(id);
      if (selectedId === id) setSelectedId(null);
      bump();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: 'hsl(var(--bg-overlay))' }}>
          {STATUS_TABS.map((t) => {
            const count = tabCounts?.[t.id];
            // Only worth a badge once a filter actually narrows things down —
            // an unfiltered count is just "how many items exist", already
            // obvious from the list itself, and would just be noise here.
            const filtered = Boolean(assignee || priority || itemType || module || reporter || search);
            return (
              <button
                key={t.id}
                onClick={() => { setStatusTab(t.id); setSelectedId(null); }}
                data-testid={`qa-status-${t.id}`}
                className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition"
                style={statusTab === t.id
                  ? { background: 'hsl(var(--bg-raised))', color: 'hsl(var(--text))' }
                  : { color: 'hsl(var(--text-muted))' }}
              >
                {t.label}
                {filtered && count !== undefined && (
                  <span
                    className="tabular rounded-full px-1.5 text-xs"
                    style={{
                      background: count > 0 ? 'hsl(var(--accent) / 0.16)' : 'hsl(var(--bg-overlay))',
                      color: count > 0 ? 'hsl(var(--accent))' : 'hsl(var(--text-faint))',
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <a
          href={api.qaTemplateUrl()}
          className="btn btn-secondary ml-auto text-xs"
          data-testid="qa-download-template"
        >
          Download template
        </a>
        <label className={`btn btn-secondary text-xs ${importing ? 'cursor-wait' : 'cursor-pointer'}`} data-testid="qa-import-label">
          {importing ? <Spinner label="Importing…" /> : 'Import sheet'}
          <input
            type="file"
            accept=".xlsx"
            className="hidden"
            disabled={importing}
            data-testid="qa-import-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setImporting(true);
              setNotice(null);
              setImportResult(null);
              try {
                const result = await api.importQaWorkbook(file);
                setImportResult(result);
                setStatusTab('backlog');
                bump();
              } catch (err) {
                setNotice(err instanceof ApiError ? err.message : String(err));
              } finally {
                setImporting(false);
              }
            }}
          />
        </label>
        <button className="btn btn-primary text-xs" onClick={() => setCreating(true)} data-testid="qa-new-item">
          + New item
        </button>
      </div>

      {notice && (
        <div className="surface px-4 py-2.5 text-sm" style={{ borderColor: 'hsl(var(--fail) / 0.4)', color: 'hsl(var(--fail))' }}>
          {notice}
        </div>
      )}

      {importResult && (
        <div className="surface flex flex-col gap-2 p-4 text-sm" data-testid="qa-import-result">
          <div className="flex items-center gap-2">
            <p>
              <strong>{importResult.created}</strong> item{importResult.created === 1 ? '' : 's'} created from the sheet.
              {importResult.problems.length > 0 && ` ${importResult.problems.length} row(s) skipped.`}
            </p>
            <button className="btn btn-ghost ml-auto text-xs" onClick={() => setImportResult(null)}>Dismiss</button>
          </div>
          {importResult.problems.length > 0 && (
            <ul className="flex flex-col gap-1 text-xs" style={{ color: 'hsl(var(--warn))' }}>
              {importResult.problems.map((p) => (
                <li key={p.row}>Row {p.row}: {p.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creating && options && (
        <ItemFormModal
          options={options}
          reporterEmail={reporterEmail}
          onClose={() => setCreating(false)}
          onSubmit={async (draft) => {
            await api.createQaItem(draft);
            setCreating(false);
            setStatusTab('backlog');
            bump();
          }}
        />
      )}

      <div className="grid gap-5 lg:grid-cols-[24rem_1fr]">
        <section className="surface flex flex-col overflow-hidden">
          <div className="flex flex-col gap-2 p-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <input
              className="field text-sm"
              placeholder="Search by title…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              data-testid="qa-search"
            />
            <div className="grid grid-cols-2 gap-2">
              <select className="field py-1 text-xs" value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="qa-filter-assignee">
                <option value="">All assignees</option>
                {options?.assignees.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select className="field py-1 text-xs" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="qa-filter-priority">
                <option value="">All priorities</option>
                {options?.priorities.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
              <select className="field py-1 text-xs" value={itemType} onChange={(e) => setItemType(e.target.value)} data-testid="qa-filter-type">
                <option value="">Bug + Enhancement</option>
                <option value="bug">Bug</option>
                <option value="enhancement">Enhancement</option>
              </select>
              <select className="field py-1 text-xs" value={module} onChange={(e) => setModule(e.target.value)} data-testid="qa-filter-module">
                <option value="">All modules</option>
                {modules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select className="field py-1 text-xs" value={reporter} onChange={(e) => setReporter(e.target.value)} data-testid="qa-filter-reporter">
                <option value="">All reporters</option>
                {options?.reporters.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {(assignee || priority || itemType || module || reporter || searchInput) && (
              <button
                className="btn btn-ghost self-start py-0.5 text-xs"
                onClick={() => { setAssignee(''); setPriority(''); setItemType(''); setModule(''); setReporter(''); setSearchInput(''); }}
                data-testid="qa-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="max-h-[36rem] overflow-auto">
            {loading && <p className="p-4 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Loading…</p>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>Nothing here.</p>
              </div>
            )}
            {items.map((item) => {
              const thumb = firstImageAttachment(item);
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(item.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(item.id); }}
                  data-testid={`qa-row-${item.id}`}
                  className="qa-row group flex w-full cursor-pointer gap-3 px-4 py-3 text-left"
                  style={{
                    borderBottom: '1px solid hsl(var(--border))',
                    background: selectedId === item.id ? 'hsl(var(--bg-overlay))' : undefined,
                  }}
                >
                  {thumb && (
                    <img
                      src={thumb.url}
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-md object-cover"
                      style={{ border: '1px solid hsl(var(--border-strong))' }}
                    />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      {/* The number to say in a standup — "did B-14 today" — not the
                          raw DB id, which carries no signal about bug vs enhancement
                          or which one came first. */}
                      <span className="text-xs font-semibold tabular" style={{ color: 'hsl(var(--accent))' }} data-testid={`qa-display-id-${item.id}`}>
                        {item.displayId}
                      </span>
                      <span className={`pill ${TYPE_PILL[item.itemType]}`}>{item.itemType}</span>
                      <span className={`pill ${PRIORITY_PILL[item.priority]}`}>{item.priority}</span>
                      {item.resolution && (
                        <span className={`pill ${RESOLUTION_PILL[item.resolution]}`} data-testid={`qa-resolution-${item.id}`}>
                          {RESOLUTION_LABEL[item.resolution]}
                        </span>
                      )}
                      <span className="ml-auto text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>{ago(item.updatedAt)}</span>
                      {/* Hidden until hover so the list stays calm to scan — same
                          pattern as the run-history delete button in Reports.tsx. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); void remove(item.id, item.displayId, item.title); }}
                        disabled={deletingId === item.id}
                        data-testid={`qa-delete-${item.id}`}
                        title={`Delete ${item.displayId}`}
                        className="shrink-0 rounded px-1.5 py-0.5 text-xs opacity-0 transition group-hover:opacity-100"
                        style={{ color: 'hsl(var(--text-faint))' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'hsl(var(--fail))'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'hsl(var(--text-faint))'; }}
                      >
                        {deletingId === item.id ? <Spinner /> : '✕'}
                      </button>
                    </div>
                    <p className="truncate text-sm font-medium">{item.title}</p>
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                      <span className="truncate">{item.assignee}</span>
                      <span>·</span>
                      <span className="truncate">{item.module}</span>
                      <span>·</span>
                      <span className="shrink-0">{item.environment}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="surface flex flex-col overflow-hidden">
          {!detail ? (
            <p className="p-6 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
              Pick an item from the list to see its detail, activity and attachments.
            </p>
          ) : options ? (
            <ItemDetail
              item={detail}
              activity={activity}
              options={options}
              busyKey={busyKey}
              onAssign={(a) => runAction('assign', () => api.reassignQaItem(detail.id, a))}
              onTransition={(target) => runAction(`transition:${target}`, () => api.transitionQaItem(detail.id, target))}
              onSave={(patch) => runAction('save', () => api.updateQaItem(detail.id, patch))}
              onUpload={(file) => runAction(`upload:${file.name}`, () => api.uploadQaAttachment(detail.id, file))}
              onDeleteAttachment={(key) => runAction(`delete-attachment:${key}`, () => api.deleteQaAttachment(detail.id, key))}
              onDelete={() => remove(detail.id, detail.displayId, detail.title)}
              deleting={deletingId === detail.id}
            />
          ) : null}
        </section>
      </div>
    </div>
  );
}

function ItemDetail({
  item, activity, options, busyKey, onAssign, onTransition, onSave, onUpload, onDeleteAttachment, onDelete, deleting,
}: {
  item: QaItem;
  activity: QaActivityEntry[];
  options: QaOptions;
  /** Which action is currently in flight — see TrackerScreen's own runAction note. */
  busyKey: string | null;
  onAssign: (assignee: string) => Promise<boolean>;
  onTransition: (target: 'in_progress' | 'testable' | 'tbd' | 'done' | 'deferred' | 'qa_found_fixed' | 'backlog') => Promise<boolean>;
  onSave: (patch: Partial<QaItemDraft>) => Promise<boolean>;
  onUpload: (file: File) => Promise<boolean>;
  onDeleteAttachment: (key: string) => Promise<boolean>;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<QaItemDraft>>({});

  useEffect(() => { setDraft({}); setEditing(false); }, [item.id]);

  const field = (key: keyof QaItemDraft) => (draft[key] as string | undefined) ?? (item[key] as string | undefined) ?? '';
  const set = (key: keyof QaItemDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  return (
    <>
      <div className="flex items-start gap-3 p-4" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular" style={{ color: 'hsl(var(--accent))' }}>{item.displayId}</span>
            <span className={`pill ${TYPE_PILL[item.itemType]}`}>{item.itemType}</span>
            <span className={`pill ${PRIORITY_PILL[item.priority]}`}>{item.priority}</span>
            {item.resolution && (
              <span className={`pill ${RESOLUTION_PILL[item.resolution]}`}>{RESOLUTION_LABEL[item.resolution]}</span>
            )}
          </div>
          <h2 className="text-base font-semibold">{item.title}</h2>
        </div>
        <div className="ml-auto flex gap-2">
          {NEXT_ACTIONS[item.status].map((a) => {
            const key = `transition:${a.target}`;
            const isBusy = busyKey === key;
            return (
              <button
                key={a.target}
                className={`btn ${a.tone === 'primary' ? 'btn-primary' : 'btn-secondary'} text-xs`}
                onClick={() => void onTransition(a.target)}
                disabled={busyKey !== null || deleting}
                data-testid={`qa-action-${a.target}`}
              >
                {isBusy ? <Spinner label={a.label} /> : a.label}
              </button>
            );
          })}
          <button
            className="btn btn-ghost text-xs"
            style={{ color: 'hsl(var(--fail))' }}
            onClick={onDelete}
            disabled={deleting || busyKey !== null}
            data-testid="qa-delete-item"
          >
            {deleting ? <Spinner label="Deleting…" /> : 'Delete'}
          </button>
        </div>
      </div>

      <div className="max-h-[40rem] overflow-auto p-4">
        <dl className="mb-4 grid gap-x-6 gap-y-1.5 text-xs" style={{ gridTemplateColumns: 'auto 1fr auto 1fr' }}>
          <dt style={{ color: 'hsl(var(--text-faint))' }}>Assigned to</dt>
          <dd className="flex items-center gap-2">
            <select
              className="field w-auto py-0.5 text-xs"
              value={item.assignee}
              onChange={(e) => void onAssign(e.target.value)}
              disabled={busyKey === 'assign'}
              data-testid="qa-assignee-select"
            >
              {options.assignees.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            {busyKey === 'assign' && <Spinner />}
          </dd>
          <dt style={{ color: 'hsl(var(--text-faint))' }}>Reporter</dt>
          <dd>{item.reporter}</dd>

          <dt style={{ color: 'hsl(var(--text-faint))' }}>Module</dt>
          <dd>{item.module}</dd>
          <dt style={{ color: 'hsl(var(--text-faint))' }}>Environment</dt>
          <dd>{item.environment}</dd>

          <dt style={{ color: 'hsl(var(--text-faint))' }}>Created</dt>
          <dd className="tabular">{new Date(item.createdAt).toLocaleString()}</dd>
          <dt style={{ color: 'hsl(var(--text-faint))' }}>Updated</dt>
          <dd className="tabular">{new Date(item.updatedAt).toLocaleString()}</dd>
        </dl>

        <div className="mb-2 flex items-center">
          <h3 className="text-sm font-semibold">Details</h3>
          <button
            className="btn btn-ghost ml-auto text-xs"
            onClick={async () => {
              if (!editing) { setEditing(true); return; }
              // Only leaves edit mode on a CONFIRMED success — otherwise the
              // client's own edits would silently vanish the instant a save
              // fails (the error still shows via the page-level notice, but
              // flipping back to read-only regardless used to throw away
              // exactly the draft they'd need to try again).
              const ok = await onSave(draft);
              if (ok) setEditing(false);
            }}
            disabled={busyKey === 'save'}
            data-testid="qa-edit-toggle"
          >
            {busyKey === 'save' ? <Spinner label="Saving…" /> : editing ? 'Save' : 'Edit'}
          </button>
          {editing && (
            <button
              className="btn btn-ghost text-xs"
              onClick={() => { setDraft({}); setEditing(false); }}
              disabled={busyKey === 'save'}
            >
              Cancel
            </button>
          )}
        </div>

        {!editing ? (
          <div className="surface-raised flex flex-col gap-2 p-3 text-sm">
            <Field label="Description" value={item.description} />
            {item.itemType === 'bug' ? (
              <>
                <Field label="Steps to reproduce" value={item.stepsToReproduce} />
                <Field label="Expected result" value={item.expectedResult} />
                <Field label="Actual result" value={item.actualResult} />
              </>
            ) : (
              <>
                <Field label="Current behavior" value={item.currentBehavior} />
                <Field label="Proposed improvement" value={item.proposedImprovement} />
                <Field label="Benefit" value={item.benefit} />
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Priority</span>
              <select className="field" value={field('priority') || item.priority} onChange={set('priority')}>
                {options.priorities.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Module</span>
              <input className="field" value={field('module')} onChange={set('module')} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Environment</span>
              <select className="field" value={field('environment') || item.environment} onChange={set('environment')}>
                {options.environments.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Description</span>
              <textarea className="field" rows={3} value={field('description')} onChange={set('description')} />
            </label>
            {item.itemType === 'bug' ? (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Steps to reproduce</span>
                  <textarea className="field" rows={3} value={field('stepsToReproduce')} onChange={set('stepsToReproduce')} />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Expected result</span>
                  <textarea className="field" rows={2} value={field('expectedResult')} onChange={set('expectedResult')} />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Actual result</span>
                  <textarea className="field" rows={2} value={field('actualResult')} onChange={set('actualResult')} />
                </label>
              </>
            ) : (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Current behavior</span>
                  <textarea className="field" rows={2} value={field('currentBehavior')} onChange={set('currentBehavior')} />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Proposed improvement</span>
                  <textarea className="field" rows={2} value={field('proposedImprovement')} onChange={set('proposedImprovement')} />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="label">Benefit</span>
                  <textarea className="field" rows={2} value={field('benefit')} onChange={set('benefit')} />
                </label>
              </>
            )}
          </div>
        )}

        <div className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">Attachments</h3>
          <label className={`btn btn-secondary text-xs ${busyKey?.startsWith('upload:') ? 'cursor-wait' : 'cursor-pointer'}`}>
            {busyKey?.startsWith('upload:') ? <Spinner label="Uploading…" /> : '+ Add attachment'}
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              disabled={busyKey?.startsWith('upload:')}
              data-testid="qa-attachment-input"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                files.forEach((f) => void onUpload(f));
                e.target.value = '';
              }}
            />
          </label>
          {item.attachments.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-3">
              {item.attachments.map((a) => {
                const isImage = a.contentType?.startsWith('image/');
                const url = api.qaAttachmentUrl(item.id, a.key);
                const removing = busyKey === `delete-attachment:${a.key}`;
                return (
                  <li key={a.key} className="flex flex-col gap-1" style={{ width: '8rem' }}>
                    <a href={url} target="_blank" rel="noreferrer">
                      {isImage ? (
                        <img
                          src={url}
                          alt={a.name}
                          className="h-24 w-32 rounded object-cover"
                          style={{ border: '1px solid hsl(var(--border))', background: 'hsl(var(--bg-overlay))' }}
                        />
                      ) : (
                        <div
                          className="flex h-24 w-32 items-center justify-center rounded text-xs underline"
                          style={{ border: '1px solid hsl(var(--border))', background: 'hsl(var(--bg-overlay))' }}
                        >
                          {a.name}
                        </div>
                      )}
                    </a>
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
                      <span className="truncate" title={a.name}>{a.name}</span>
                      <span className="ml-auto shrink-0" style={{ color: 'hsl(var(--text-faint))' }}>{bytes(a.size)}</span>
                    </div>
                    <button
                      className="btn btn-ghost py-0.5 text-xs"
                      onClick={() => void onDeleteAttachment(a.key)}
                      disabled={busyKey !== null}
                    >
                      {removing ? <Spinner label="Removing…" /> : 'Remove'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">Activity</h3>
          {activity.length === 0 ? (
            <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>No activity yet.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {activity.map((a) => (
                <li key={a.id} className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
                  <span className="tabular" style={{ color: 'hsl(var(--text-faint))' }}>{ago(a.at)}</span>{' — '}
                  {describeActivity(a)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function describeActivity(a: QaActivityEntry): string {
  if (a.action === 'created') return `${a.actor} created this, assigned to ${a.toValue}`;
  if (a.action === 'assigned') return `${a.actor} reassigned from ${a.fromValue} to ${a.toValue}`;
  return `${a.actor} moved this from ${a.fromValue} to ${a.toValue}`;
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <p className="label">{label}</p>
      <p className="whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export function ItemFormModal({
  options, reporterEmail, initial, lockType, title: modalTitle, onClose, onSubmit,
}: {
  options: QaOptions;
  reporterEmail: string;
  /** Pre-fills the form — used when raising a bug straight from a failed test
   *  (see Reports.tsx), where the title/module/environment/repro are already known. */
  initial?: Partial<QaItemDraft>;
  /** Disables the Bug/Enhancement choice — a bug raised from a failed test is
   *  always a bug, so there is nothing to choose. */
  lockType?: boolean;
  title?: string;
  onClose: () => void;
  onSubmit: (draft: QaItemDraft) => Promise<void>;
}) {
  const [itemType, setItemType] = useState<'bug' | 'enhancement'>(initial?.itemType ?? 'bug');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [stepsToReproduce, setStepsToReproduce] = useState(initial?.stepsToReproduce ?? '');
  const [expectedResult, setExpectedResult] = useState(initial?.expectedResult ?? '');
  const [actualResult, setActualResult] = useState(initial?.actualResult ?? '');
  const [currentBehavior, setCurrentBehavior] = useState(initial?.currentBehavior ?? '');
  const [proposedImprovement, setProposedImprovement] = useState(initial?.proposedImprovement ?? '');
  const [benefit, setBenefit] = useState(initial?.benefit ?? '');
  const [priority, setPriority] = useState<string>(initial?.priority ?? options.priorities[1] ?? options.priorities[0]);
  const [assignee, setAssignee] = useState(initial?.assignee ?? options.assignees[0] ?? '');
  const [reporter, setReporter] = useState(initial?.reporter ?? reporterEmail);
  const [module, setModule] = useState(initial?.module ?? '');
  const [environment, setEnvironment] = useState<string>(initial?.environment ?? options.environments[0] ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setError(null);
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!module.trim()) { setError('Module is required.'); return; }
    setSaving(true);
    try {
      await onSubmit({
        title, itemType, description,
        stepsToReproduce: itemType === 'bug' ? stepsToReproduce : undefined,
        expectedResult: itemType === 'bug' ? expectedResult : undefined,
        actualResult: itemType === 'bug' ? actualResult : undefined,
        currentBehavior: itemType === 'enhancement' ? currentBehavior : undefined,
        proposedImprovement: itemType === 'enhancement' ? proposedImprovement : undefined,
        benefit: itemType === 'enhancement' ? benefit : undefined,
        priority: priority as QaItemDraft['priority'],
        assignee,
        reporter,
        module,
        environment: environment as QaItemDraft['environment'],
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      data-testid="qa-item-modal"
    >
      <div className="surface my-8 w-full max-w-xl p-0">
        <div className="flex items-baseline gap-3 p-4" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 className="text-base font-semibold">{modalTitle ?? 'New item'}</h2>
          <button className="btn btn-ghost ml-auto text-xs" onClick={onClose}>Close</button>
        </div>

        <div className="flex flex-col gap-3 p-5">
          {error && <p className="text-sm" style={{ color: 'hsl(var(--fail))' }}>{error}</p>}

          <fieldset className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={itemType === 'bug'} disabled={lockType} onChange={() => setItemType('bug')} /> Bug
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={itemType === 'enhancement'} disabled={lockType} onChange={() => setItemType('enhancement')} /> Enhancement
            </label>
            {lockType && <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>Raised from a failed test — always a bug.</span>}
          </fieldset>

          <label className="flex flex-col gap-1 text-sm">
            <span className="label">Title</span>
            <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="qa-form-title" />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="label">Description</span>
            <textarea className="field" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>

          {itemType === 'bug' ? (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Steps to reproduce</span>
                <textarea className="field" rows={3} value={stepsToReproduce} onChange={(e) => setStepsToReproduce(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Expected result</span>
                <textarea className="field" rows={2} value={expectedResult} onChange={(e) => setExpectedResult(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Actual result</span>
                <textarea className="field" rows={2} value={actualResult} onChange={(e) => setActualResult(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Current behavior</span>
                <textarea className="field" rows={2} value={currentBehavior} onChange={(e) => setCurrentBehavior(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Proposed improvement</span>
                <textarea className="field" rows={2} value={proposedImprovement} onChange={(e) => setProposedImprovement(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="label">Benefit</span>
                <textarea className="field" rows={2} value={benefit} onChange={(e) => setBenefit(e.target.value)} />
              </label>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Priority</span>
              <select className="field" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {options.priorities.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Assignee</span>
              <select className="field" value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="qa-form-assignee">
                {options.assignees.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Reporter</span>
              <input className="field" value={reporter} onChange={(e) => setReporter(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Module</span>
              <input className="field" list="qa-modules" value={module} onChange={(e) => setModule(e.target.value)} data-testid="qa-form-module" />
              <datalist id="qa-modules">
                {options.modules.map((m) => <option key={m} value={m} />)}
              </datalist>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Environment</span>
              <select className="field" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                {options.environments.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </label>
          </div>

          <button className="btn btn-primary mt-2" onClick={submit} disabled={saving} data-testid="qa-form-submit">
            {saving ? <Spinner label="Saving…" /> : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
